/**
 * @file Stages Context Provider
 * @description Implementation of the StagesContext provider.
 * 
 * This file implements the StagesContext provider component, which fetches
 * stage data from the API and provides it along with utility functions
 * to components throughout the application.
 *
 * Reads GET /stages (api/stages getStages) under [STAGES_QUERY_KEY,
 * language], the default five minutes; the profile's StageSettings
 * invalidates it after an edit. Each stage's name is the server's, in the
 * app's language (a catalogue stage by its word, a gardener's own name as
 * they wrote it), read again when the language changes; each stage's colour
 * is registered for getStageTheme (theme/stageThemes) during render.
 *
 * Mounted per route by App; read with shared/hooks/useStagesContext.
 *
 * @module context/StagesContext
 */

import { ReactNode, useCallback, useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { STAGES_QUERY_KEY } from '@/config/tunables/ui';
import { getStages } from '../api/stages';
import { StagesContext } from './StagesContextDefinition';
import { StageId } from '../types/stage';
import { registerStageColors } from '../theme/stageThemes';
import { useTranslation } from '../hooks/useTranslation';
import { debugLog } from '@/config/config';

/**
 * StagesProvider component
 * @component
 * @description Provider component for the StagesContext
 * 
 * This component fetches stage data from the API and provides it to all
 * child components via React Context. It also provides utility functions
 * for working with stages.
 * 
 * @param {object} props - Component props
 * @param {ReactNode} props.children - Child components
 * @returns {JSX.Element} Provider component
 */
export function StagesProvider({ children }: { children: ReactNode }): JSX.Element {
  const { i18n: { language } } = useTranslation();
  // The stages, named by the server in the app's language: read again when the language changes, the last
  // language's kept on screen (colours and pickers) until the new names come.
  const { data: stages, isLoading, error, refetch } = useQuery({
    queryKey: [STAGES_QUERY_KEY, language],
    queryFn: () => getStages(language),
    placeholderData: keepPreviousData,
  });

  /*
   * Publish the colours these stages resolved to.
   *
   * Done during render rather than in an effect so that the first paint after
   * a fetch already has them: getStageTheme is called straight from render in
   * several components, and an effect would leave one frame painted in the
   * built-in colours before correcting itself.
   */
  registerStageColors(stages);

  /**
   * Get a stage by its ID
   * 
   * @function getStageById
   * @description Finds a stage in the stages array by its ID
   * 
   * @param {StageId} stageId - The ID of the stage to retrieve
   * @returns {Stage | null} The stage object if found, null otherwise
   */
  const getStageById = useCallback((stageId: StageId) => {
    if (!stages) return null;
    const stage = stages.find(stage => stage.stage_id === stageId) || null;
    debugLog('StageService', `getStageById - Looking for stage ID ${stageId}:`, stage);
    return stage;
  }, [stages]);

  // One object per change, not per render, so readers re-render only when
  // the stages do.
  const value = useMemo(() => ({
    stages,
    isLoading,
    error: error as Error | null,
    refetchStages: refetch,
    getStageById,
  }), [stages, isLoading, error, refetch, getStageById]);

  return (
    <StagesContext.Provider value={value}>
      {children}
    </StagesContext.Provider>
  );
}